import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, CheckCircle2, ChevronLeft, ChevronRight, Loader2, MapPin, Search } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const StateBarPage = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [searchName, setSearchName] = useState('');
  const [barNumber, setBarNumber] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState(true);
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('state-bar', 'view');
    loadStates();
  }, [trackEvent]);
  
  const loadStates = async () => {
    try {
      const res = await axios.get(`${API}/state-bar/states`);
      setStates(res.data.states || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStates(false);
    }
  };
  
  const lookupAttorney = async () => {
    if (!searchName || !selectedState) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/state-bar/lookup`, {
        name: searchName,
        state: selectedState,
        bar_number: barNumber || null
      });
      setLookupResult(res.data);
      trackEvent('state-bar', 'lookup', { state: selectedState });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const statesWithAPI = states.filter(s => s.api_available);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="state-bar-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>State Bar Lookup</h1>
            <Badge className="bg-indigo-600">All 50 States</Badge>
          </div>
          <p className="text-muted-foreground">Attorney verification and license status lookup across all U.S. state bar associations</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Lookup Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Attorney Lookup
              </CardTitle>
              <CardDescription>Search for attorney license status and discipline records</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>State</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {states.map(s => (
                      <SelectItem key={s.code} value={s.code}>
                        <div className="flex items-center gap-2">
                          <span>{s.code}</span>
                          <span className="text-muted-foreground">-</span>
                          <span>{s.name}</span>
                          {s.api_available && <Badge variant="outline" className="text-xs">API</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Attorney Name</Label>
                <Input
                  value={searchName}
                  onChange={e => setSearchName(e.target.value)}
                  placeholder="e.g., John Smith"
                />
              </div>
              <div>
                <Label>Bar Number (Optional)</Label>
                <Input
                  value={barNumber}
                  onChange={e => setBarNumber(e.target.value)}
                  placeholder="e.g., 123456"
                />
              </div>
              <Button 
                onClick={lookupAttorney} 
                disabled={loading || !searchName || !selectedState}
                className="w-full"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Search State Bar
              </Button>
            </CardContent>
          </Card>
          
          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle>Lookup Result</CardTitle>
            </CardHeader>
            <CardContent>
              {lookupResult ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge className={lookupResult.status === 'lookup_info' ? 'bg-green-500' : 'bg-amber-500'}>
                      {lookupResult.status}
                    </Badge>
                    <span className="font-medium">{lookupResult.bar_name}</span>
                  </div>
                  
                  {lookupResult.lookup_url && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800 mb-2">{lookupResult.instructions}</p>
                      <a
                        href={lookupResult.lookup_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                      >
                        <MapPin className="h-4 w-4" />
                        Open State Bar Directory
                        <ChevronRight className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                  
                  {lookupResult.discipline_url && (
                    <a
                      href={lookupResult.discipline_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:underline text-sm flex items-center gap-1"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Check Discipline Records
                    </a>
                  )}
                  
                  {lookupResult.notes && (
                    <p className="text-sm text-muted-foreground">{lookupResult.notes}</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Enter attorney details and search to see results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* States with API Access */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              States with Direct API Access ({statesWithAPI.length})
            </CardTitle>
            <CardDescription>These states provide enhanced lookup capabilities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {statesWithAPI.map(s => (
                <Badge key={s.code} variant="outline" className="cursor-pointer hover:bg-slate-100" onClick={() => setSelectedState(s.code)}>
                  {s.code} - {s.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* All States Grid */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>All State Bar Associations ({states.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {states.map(s => (
                  <Button
                    key={s.code}
                    variant="outline"
                    size="sm"
                    className={`justify-start ${selectedState === s.code ? 'border-blue-500 bg-blue-50' : ''}`}
                    onClick={() => setSelectedState(s.code)}
                  >
                    <span className="font-bold mr-2">{s.code}</span>
                    {s.api_available && <Badge variant="outline" className="text-xs ml-auto">API</Badge>}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
};


export default StateBarPage;
